import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fileUrl } from "@/lib/api";
import { yen, area, dateJP, num } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { ROOM_STATUS, PROPERTY_TYPES } from "@/lib/constants";
import { ArrowLeft, Building2, Loader2, MapPin, Map as MapIcon, ExternalLink } from "lucide-react";

const TYPE_LABEL = Object.fromEntries(PROPERTY_TYPES.map((t) => [t.value, t.label]));

function Field({ label, value }) {
  return (
    <div className="border-b border-border py-2.5 flex justify-between gap-4">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right">{value ?? "-"}</span>
    </div>
  );
}

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [p, r, l] = await Promise.all([
          api.get(`/properties/${id}`),
          api.get(`/rooms?property_id=${id}`),
          api.get(`/loans`),
        ]);
        setProperty(p.data);
        setRooms(r.data);
        setLoans(l.data.filter((x) => x.property_id === id));
      } catch {} finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>;
  if (!property) return <div className="text-muted-foreground">物件が見つかりません</div>;

  return (
    <div className="animate-fade-in">
      <Button variant="ghost" onClick={() => navigate("/properties")} className="mb-4 -ml-2" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4 mr-1.5" />物件一覧へ戻る
      </Button>

      <div className="bg-white border border-border rounded-sm overflow-hidden mb-6">
        <div className="h-48 md:h-64 bg-gray-100 relative">
          {property.photo_url ? (
            <img src={fileUrl(property.photo_url)} alt={property.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-300"><Building2 className="w-16 h-16" /></div>
          )}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-5">
            <div className="flex items-center gap-2 mb-1">
              <span className="bg-brand text-white text-xs px-2 py-0.5 rounded-sm">{TYPE_LABEL[property.type]}</span>
            </div>
            <h1 className="font-display text-2xl md:text-3xl font-medium text-white">{property.name}</h1>
            <div className="flex items-center gap-1.5 text-gray-200 text-sm mt-1"><MapPin className="w-3.5 h-3.5" />{property.address}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-border rounded-sm p-5">
          <h3 className="text-sm font-medium mb-3">基本情報</h3>
          <Field label="郵便番号" value={property.postal_code} />
          <Field label="構造" value={property.structure} />
          <Field label="階数" value={property.floors ? `${property.floors}階` : "-"} />
          <Field label="土地面積" value={area(property.land_area)} />
          <Field label="建物面積" value={area(property.building_area)} />
          <Field label="築年月" value={property.built_date} />
          <Field label="取得日" value={dateJP(property.acquired_date)} />
          <Field label="管理会社" value={property.management_company} />
        </div>

        <div className="bg-white border border-border rounded-sm p-5">
          <h3 className="text-sm font-medium mb-3">資産価値</h3>
          <Field label="購入価格" value={yen(property.purchase_price)} />
          <Field label="現在評価額" value={yen(property.current_value)} />
          <Field label="土地価格" value={yen(property.land_price)} />
          <Field label="建物価格" value={yen(property.building_price)} />
          <Field label="固定資産税(年)" value={yen(property.property_tax)} />
        </div>

        <div className="bg-white border border-border rounded-sm p-5">
          <h3 className="text-sm font-medium mb-3">融資（ローン）</h3>
          {loans.length === 0 ? (
            <p className="text-sm text-muted-foreground">登録なし</p>
          ) : loans.map((l) => (
            <div key={l.id} className="mb-3">
              <Field label="金融機関" value={l.lender} />
              <Field label="現在残高" value={yen(l.current_balance)} />
              <Field label="毎月返済額" value={yen(l.monthly_payment)} />
              <Field label="金利" value={`${l.interest_rate}%`} />
            </div>
          ))}
          {property.note && (
            <div className="mt-3 pt-3 border-t border-border">
              <div className="text-xs text-muted-foreground mb-1">備考</div>
              <p className="text-sm">{property.note}</p>
            </div>
          )}
        </div>
      </div>

      {(property.building_photos?.length || property.floor_plans?.length) ? (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {property.building_photos?.length ? (
            <div className="bg-white border border-border rounded-sm p-5" data-testid="property-building-photos">
              <h3 className="text-sm font-medium mb-3">建物写真</h3>
              <div className="grid grid-cols-3 gap-2">
                {property.building_photos.map((u, i) => (
                  <a key={i} href={fileUrl(u)} target="_blank" rel="noreferrer" className="block aspect-square rounded-sm overflow-hidden border border-border">
                    <img src={fileUrl(u)} alt="" className="w-full h-full object-cover hover:opacity-90 transition-opacity" />
                  </a>
                ))}
              </div>
            </div>
          ) : null}
          {property.floor_plans?.length ? (
            <div className="bg-white border border-border rounded-sm p-5" data-testid="property-floor-plans">
              <h3 className="text-sm font-medium mb-3">間取図</h3>
              <div className="grid grid-cols-2 gap-2">
                {property.floor_plans.map((u, i) => (
                  <a key={i} href={fileUrl(u)} target="_blank" rel="noreferrer" className="block rounded-sm overflow-hidden border border-border bg-gray-50">
                    <img src={fileUrl(u)} alt="" className="w-full h-40 object-contain" />
                  </a>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {(property.google_map_url || property.address) && (
        <div className="mt-6 bg-white border border-border rounded-sm p-5" data-testid="property-map">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium flex items-center gap-1.5"><MapIcon className="w-4 h-4 text-brand" />地図（所在地）</h3>
            <a href={property.google_map_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(property.address || property.name)}`} target="_blank" rel="noreferrer" className="text-brand text-sm inline-flex items-center gap-1 hover:underline" data-testid="property-map-open">
              Googleマップで開く<ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
          <div className="rounded-sm overflow-hidden border border-border">
            <iframe title="map" width="100%" height="320" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              src={`https://www.google.com/maps?q=${encodeURIComponent(property.address || property.name)}&output=embed`} />
          </div>
        </div>
      )}

      <div className="mt-6 bg-white border border-border rounded-sm p-5">
        <h3 className="text-sm font-medium mb-4">部屋・区画一覧（{rooms.length}戸）</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-2.5">部屋番号</th><th className="px-4 py-2.5">間取り</th>
                <th className="px-4 py-2.5">面積</th><th className="px-4 py-2.5">賃料</th><th className="px-4 py-2.5">状況</th>
              </tr>
            </thead>
            <tbody>
              {rooms.map((r) => (
                <tr key={r.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-2.5 font-medium">{r.room_number}</td>
                  <td className="px-4 py-2.5">{r.layout}</td>
                  <td className="px-4 py-2.5">{area(r.area)}</td>
                  <td className="px-4 py-2.5">{yen(r.rent)}</td>
                  <td className="px-4 py-2.5"><StatusBadge value={r.status} map={ROOM_STATUS} /></td>
                </tr>
              ))}
              {rooms.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">部屋の登録がありません</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
